'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Button } from '@sarkiai/ui'
import { Zap, Globe, Share2, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslations } from '@/components/I18nProvider'
import Masonry from '@/components/Masonry'

export function VCardDetail() {
  const t = useTranslations('services')

  const features = [
    { icon: Zap, title: t('vcard.feature1.title'), desc: t('vcard.feature1.desc') },
    { icon: Globe, title: t('vcard.feature2.title'), desc: t('vcard.feature2.desc') },
    { icon: Share2, title: t('vcard.feature3.title'), desc: t('vcard.feature3.desc') },
  ]

  return (
    <section className="py-20 relative overflow-hidden px-4 lg:px-12">
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex-1 w-full space-y-8"
          >
            <h2 className="text-2xl md:text-4xl uppercase tracking-tighter leading-tight text-foreground">
              {t('vcard.hero.title')}
            </h2>

            <p className="text-md text-muted-foreground leading-relaxed">
              {t('vcard.hero.subtitle')}
            </p>

            <div className="grid grid-cols-1 gap-6 pt-4">
              {features.map((f, i) => (
                <div key={i} className="flex gap-6 items-center group">
                  <div className="shrink-0">
                    <div className="w-14 h-14 rounded-2xl bg-transparent border border-black/10 dark:border-white/20 flex items-center justify-center shadow-lg shadow-black/5 transition-transform group-hover:scale-110">
                      <f.icon className="w-7 h-7 text-black dark:text-white" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <h4 className="uppercase tracking-tight text-xl">{f.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-8">
              <Button asChild size="default" className="rounded-2xl px-8 h-12 text-xs bg-black text-white hover:bg-black/90 uppercase tracking-widest transition-all">
                <Link href="/vcard">
                  {t('vcard.explorer')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex-1 w-full min-h-[600px]"
          >
            <Masonry
              items={[
                { id: '5', img: '/assets/covers/vcard-std.webp', url: '/vcard', height: 500 },
                { id: '1', img: '/assets/covers/vcard-by-djotta-cover-01.webp', url: '/vcard', height: 150 },
                { id: '2', img: '/assets/covers/vcard-douanes-cm.webp', url: '/vcard', height: 450 },
                { id: '3', img: '/assets/covers/vcard-pefi.png', url: '/vcard', height: 150 },
                { id: '7', img: '/assets/covers/vcard-v2.webp', url: '/vcard', height: 450 },
                { id: '13', img: '/assets/covers/djotta-cards-cover.webp', url: '/vcard', height: 150 },
                { id: '4', img: '/assets/covers/vcard-picardi.webp', url: '/vcard', height: 550 },
                { id: '14', img: '/assets/covers/djotta-partner.webp', url: '/vcard', height: 150 },
                { id: '6', img: '/assets/covers/vcard-v1.webp', url: '/vcard', height: 450 },
                { id: '10', img: '/assets/covers/vcard3.webp', url: '/vcard', height: 450 },
                { id: '11', img: '/assets/covers/vcard4.webp', url: '/vcard', height: 450 },
              ]}
              ease="power3.out"
              duration={0.6}
              stagger={0.05}
              animateFrom="bottom"
              scaleOnHover
              hoverScale={0.95}
              blurToFocus
              colorShiftOnHover={false}
            />
          </motion.div>
        </div>
      </div>
    </section>
  )
}
